import { useEffect, useReducer, useState } from 'react';
import { Form, Formik } from 'formik';
import { indexOf } from 'lodash';
import { useRouter } from 'next/router';
import { useCookie } from 'react-use';
import {
  ButtonPrimary,
  HorizontalRule,
  LoadingIndicator,
  spacer8,
  Type,
} from '@spotify-internal/encore-web';
import { useT } from '@spotify-internal/i18n-core';
import Select, { MultiValue } from '@spotify-internal/react-select';
import { sendEvent } from '@spotify-internal/s4mp-page-analytics';
import { routeExposedLocales } from '@spotify-internal/s4mp-shared';
import { useCreateCatalogExport } from '@hooks/useCreateCatalogExport';
import { useCurrentUserInfo } from '@hooks/useCurrentUserInfo';
import { useHasMusicVideoAccess } from '@hooks/useHasMusicVideoAccess';
import { FETCH_STATUS } from '@hooks/useSWRFetch/types';
import {
  buildAddSnackbarAction,
  SnackBarColorSet,
} from '@context/SnackbarNotifications/actions';
import { useSnackbarNotifications } from '@context/SnackbarNotifications/SnackbarNotifications';
import { DataFreshnessDisplay } from '@components/Catalog/ContentArea/CatalogExports/DataFreshnessDisplay';
import { FormFieldDropdown } from '@components/Catalog/ContentArea/CatalogExports/FormFieldDropdown';
import { EVENT_ACTION, EVENT_CATEGORY } from '@lib/ga/types';
import { GA_PRODUCT_CATALOG } from '@helpers/constants';
import { fromMomentObjectToISO } from '@helpers/dateTimeUtils';
import {
  getLanguageAsMomentLocale,
  setLanguagePreference,
} from '@helpers/translate';
import { isEmptyString } from '@helpers/utils';
import {
  CreateCatalogExportRequest__Output,
  ExportType__Output,
} from '@generated/grpc/spotify/catalogexport/v1';
import { _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output } from '@generated/grpc/spotify/s4mpview/v1';
import { DateRangeDropdown } from './DateRangeDropdown';
import { FilenameInput } from './FilenameInput';
import { handleValidate } from './handleValidate';
import { OnboardingPopoverOne } from './OnboardingPopovers/OnboardingPopoverOne';
import { OnboardingPopoverThree } from './OnboardingPopovers/OnboardingPopoverThree';
import { OnboardingPopoverTwo } from './OnboardingPopovers/OnboardingPopoverTwo';
import { initialState, reducer } from './reducer';
import {
  ButtonContainer,
  Content,
  InputsContainer,
  LoadingIndicatorContainer,
  StyledFormGroup,
  StyledWrapper,
  TitleContainer,
  encoreSelectChipStyles,
  TitleAndFreshnessContainer,
  DataFreshnessContainer,
} from './styles';
import { DATE_RANGE_TYPE } from './types';
import { FormValues } from './types';

export type OptionType = {
  label: string;
  value: string;
};

const resourcesToSortedOptionType = (
  resources: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output[],
): MultiValue<OptionType> => {
  return resources
    .map(
      (resource: _spotify_s4mpview_v1_WhoAmIResponse_NamedEntity__Output) => ({
        value: resource.id,
        label: resource.name,
      }),
    )
    .sort((resourceA, resourceB) => {
      if (resourceA.label.toLowerCase() < resourceB.label.toLowerCase()) {
        return -1;
      }
      if (resourceA.label.toLowerCase() > resourceB.label.toLowerCase()) {
        return 1;
      }
      return 0;
    });
};

export const CatalogExports = () => {
  const t = useT();
  const {
    status,
    actions: { createCatalogExportRequest },
  } = useCreateCatalogExport();
  const [, dispatchSnackbar] = useSnackbarNotifications();
  const { locale } = useRouter();
  const { data: userInfo } = useCurrentUserInfo();
  const { hasAccess: hasMusicVideoAccess } = useHasMusicVideoAccess();
  const userFeeds =
    Array.isArray(userInfo?.feeds) && userInfo?.feeds.length! > 0
      ? resourcesToSortedOptionType(userInfo!.feeds)
      : [];
  const userLicensors =
    Array.isArray(userInfo?.licensors) && userInfo?.licensors.length! > 0
      ? resourcesToSortedOptionType(userInfo!.licensors)
      : [];

  const localeToUse = !locale || locale === 'en-US' ? 'en' : locale;
  // Dynamically import moment locales based on the exposedLocales from S4MP
  for (const language of routeExposedLocales) {
    const momentLang = getLanguageAsMomentLocale(language);
    if (momentLang !== '') {
      require(`moment/locale/${momentLang}`);
    }
  }

  setLanguagePreference(locale);

  const [onboardingStep, setOnboardingStep] = useState<number | null>(null);
  // S4MP will set cookie to 'true' when user navigates from Release Notes
  const [onboardingCookie, , deleteCookie] = useCookie(
    'catalogExportsOnboardingVisible',
  );
  const [selectedExportType, setSelectedExportType] = useState<number>(0);
  const [licensorMenuIsOpen, setLicensorMenuOpen] = useState(false);
  const [licensorIdValues, setLicensorIds] = useState<MultiValue<OptionType>>(
    [],
  );
  const [feedMenuIsOpen, setFeedMenuOpen] = useState(false);
  const [feedIdValues, setFeedIds] = useState<MultiValue<OptionType>>([]);

  const [
    {
      selectedDateRangeTypeId,
      selectedDateRange,
      dateRangeInFilename,
      lastSavedCustomDateRange,
    },
    dispatch,
  ] = useReducer(reducer, initialState);

  const customDateRangeFormatted = `${fromMomentObjectToISO(
    lastSavedCustomDateRange.startDate,
  )} — ${fromMomentObjectToISO(lastSavedCustomDateRange.endDate)}`;

  let exportTypeItems = [
    {
      text: t(
        'atlas.labels.export_types.album',
        'Album Export',
        'Type of export',
      ),
      id: 'ALBUM' as ExportType__Output,
    },
    {
      text: t(
        'atlas.labels.export_types.track',
        'Track Export',
        'Type of export',
      ),
      id: 'TRACK' as ExportType__Output,
    },
  ];

  if (hasMusicVideoAccess) {
    exportTypeItems = exportTypeItems.concat([
      {
        text: t(
          'atlas.labels.export_types.video',
          'Video Export',
          'Type of export (video)',
        ),
        id: 'VIDEO' as ExportType__Output,
      },
    ]);
  }

  const isAllTimeDateRangeType =
    selectedDateRangeTypeId === DATE_RANGE_TYPE.ALL_TIME;
  const isCustomDateRangeType =
    selectedDateRangeTypeId === DATE_RANGE_TYPE.CUSTOM_DATES;
  const allTimeDateRangeInFilename = t(
    'atlas.date_range_types.all_time',
    'All time',
    'Refers to all time',
  );

  const handleExportTypeChange = (id: string) => {
    const selectedItem = exportTypeItems.find(item => item.id === id);
    const selectedIndex = indexOf(exportTypeItems, selectedItem);

    if (selectedItem) {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.SELECT_EXPORT_TYPE,
        eventLabel: `Selected export type: ${selectedItem.text}`,
        product: GA_PRODUCT_CATALOG,
      });

      setSelectedExportType(selectedIndex);
    }
  };

  const renderDateRangeInFilename = () => {
    if (isCustomDateRangeType) {
      return lastSavedCustomDateRange.startDate &&
        lastSavedCustomDateRange.endDate
        ? ` - ${customDateRangeFormatted}`
        : '';
    }
    if (isAllTimeDateRangeType) {
      return ` - ${allTimeDateRangeInFilename}`;
    }
    return ` - ${dateRangeInFilename}`;
  };

  let exportTypeName = t(
    'atlas.labels.export_types.album',
    'Album Export',
    'Type of export',
  );
  if (selectedExportType === 1) {
    exportTypeName = t(
      'atlas.labels.export_types.track',
      'Track Export',
      'Type of export',
    );
  } else if (selectedExportType === 2) {
    exportTypeName = t(
      'atlas.labels.export_types.video',
      'Video Export',
      'Type of export (video)',
    );
  }

  const defaultExportFileName = `Spotify - ${exportTypeName}${renderDateRangeInFilename()}`;

  const handleSubmit = async (formValues: FormValues) => {
    const endDateEndOfDay = new Date(selectedDateRange.endDate);
    endDateEndOfDay.setUTCHours(23, 59, 59, 999);

    // This form is primarily using Formik for field validation
    // The values submitted to the BE come from state, excluding exportFileName
    const body: CreateCatalogExportRequest__Output = {
      exportType: exportTypeItems[selectedExportType].id,
      startDate: new Date(selectedDateRange.startDate).toISOString(),
      endDate: new Date(endDateEndOfDay).toISOString(),
      exportFileName: isEmptyString(formValues.exportFileName)
        ? defaultExportFileName
        : formValues.exportFileName,
      languageLocale: localeToUse,
      feedIds:
        feedIdValues.length > 0 ? feedIdValues.map(feed => feed.value) : [],
      licensorIds:
        licensorIdValues.length > 0
          ? licensorIdValues.map(licensor => licensor.value)
          : [],
    };

    await createCatalogExportRequest(body).then(destinationEmailAddress => {
      if (isEmptyString(destinationEmailAddress)) {
        sendEvent({
          eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
          eventAction: EVENT_ACTION.CLICK,
          eventLabel: 'Error occurred requesting to create catalog export',
          product: GA_PRODUCT_CATALOG,
        });
        dispatchSnackbar(
          buildAddSnackbarAction(
            t(
              'atlas.catalog_exports.snackbars.creating_export_error',
              `We couldn't create your export. Try again.`,
              'Snackbar text that indicates an error occurred with the export',
            ),
            SnackBarColorSet.NEGATIVE,
          ),
        );
      } else {
        if (!isEmptyString(formValues.exportFileName)) {
          sendEvent({
            eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
            eventAction: EVENT_ACTION.CLICK,
            eventLabel: `${formValues.exportFileName}`,
            product: GA_PRODUCT_CATALOG,
          });
        }

        sendEvent({
          eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
          eventAction: EVENT_ACTION.CLICK,
          eventLabel: 'Submitted to create catalog export',
          product: GA_PRODUCT_CATALOG,
        });

        dispatchSnackbar(
          buildAddSnackbarAction(
            t(
              'atlas.catalog_exports.snackbars.creating_export',
              `Creating export. You can leave this page. We'll send it to {destinationEmailAddress} when it's ready.`,
              'Snackbar text that indicates an export is being created',
              { destinationEmailAddress },
            ),
          ),
        );
      }
    });
  };

  const onCompleteOnboarding = () => {
    setOnboardingStep(null);
  };

  const handleLicensorSelectChange = (option: MultiValue<OptionType>) => {
    if (option.length === 0 && licensorIdValues.length > 1) {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.CHANGE_LICENSOR_FILTER,
        eventLabel: `Cleared licensor filter`,
        product: GA_PRODUCT_CATALOG,
      });
    } else if (option.length < licensorIdValues.length) {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.CHANGE_LICENSOR_FILTER,
        eventLabel: `Removed licensor from filter`,
        product: GA_PRODUCT_CATALOG,
      });
    } else {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.CHANGE_LICENSOR_FILTER,
        eventLabel: `Add licensor to filter`,
        product: GA_PRODUCT_CATALOG,
      });
    }

    setLicensorIds(option);
  };

  const handleFeedSelectChange = (option: MultiValue<OptionType>) => {
    if (option.length === 0 && feedIdValues.length > 1) {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.CHANGE_LICENSOR_FILTER,
        eventLabel: `Cleared feed filter`,
        product: GA_PRODUCT_CATALOG,
      });
    } else if (option.length < feedIdValues.length) {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.CHANGE_LICENSOR_FILTER,
        eventLabel: `Removed feed from filter`,
        product: GA_PRODUCT_CATALOG,
      });
    } else {
      sendEvent({
        eventCategory: EVENT_CATEGORY.CATALOG_EXPORTS_FORM,
        eventAction: EVENT_ACTION.CHANGE_LICENSOR_FILTER,
        eventLabel: `Add feed to filter`,
        product: GA_PRODUCT_CATALOG,
      });
    }

    setFeedIds(option);
  };

  useEffect(() => {
    if (onboardingCookie) {
      setOnboardingStep(1);
      deleteCookie();
    }
  }, [onboardingCookie, deleteCookie, setOnboardingStep]);

  return (
    <Content>
      <TitleContainer>
        <Type variant="cello" paddingBottom={spacer8}>
          {t(
            'atlas.catalog_exports.create_an_export',
            'Create an export',
            'Title for directions section',
          )}
        </Type>
        <TitleAndFreshnessContainer>
          <StyledWrapper
            dangerouslySetInnerHTML={{
              __html: t(
                'atlas.catalog_exports.tool_description',
                `Exports will be emailed to you upon creation and will include updates and inserts for the date range selected. Exported data may be up to 48 hours old. If the product you're looking for isn't available, <a href="https://musicproviders.spotify.com/deliveries">check out Deliveries</a>.`,
                'Description of this catalog export tool',
              ),
            }}
          />
          <DataFreshnessContainer>
            <DataFreshnessDisplay exportTypeIndex={selectedExportType} />
          </DataFreshnessContainer>
        </TitleAndFreshnessContainer>
      </TitleContainer>
      <Formik
        initialValues={{
          exportType: exportTypeItems[0].id,
          startDate: '',
          endDate: '',
          exportFileName: '',
        }}
        validate={values => handleValidate(values, t, isCustomDateRangeType)}
        onSubmit={(values, {}) => {
          handleSubmit(values);
        }}
      >
        {({ values, handleChange, handleBlur, errors, touched }) => {
          return (
            <Form>
              <InputsContainer>
                <FormFieldDropdown
                  label={t(
                    'atlas.labels.export_type',
                    'Export type',
                    'Input label for Export type (i.e. Album Export, Track Export)',
                  )}
                  id="export-type"
                  name="exportType"
                  items={exportTypeItems}
                  onChange={handleExportTypeChange}
                />
                {onboardingStep === 1 && (
                  <OnboardingPopoverOne
                    setOnboardingStep={setOnboardingStep}
                    onComplete={onCompleteOnboarding}
                  />
                )}
                <DateRangeDropdown
                  selectedDateRangeTypeId={selectedDateRangeTypeId}
                  lastSavedCustomDateRange={lastSavedCustomDateRange}
                  errors={errors}
                  touched={touched}
                  dispatch={dispatch}
                  onboardingPopover={
                    onboardingStep === 2 ? (
                      <OnboardingPopoverTwo
                        setOnboardingStep={setOnboardingStep}
                        onComplete={onCompleteOnboarding}
                      />
                    ) : undefined
                  }
                />
                <StyledFormGroup
                  indicator="optional"
                  label={t(
                    'atlas.labels.licensors',
                    'Licensors',
                    'Label for Licensors',
                  )}
                  labelFor="licensor-multi-select"
                >
                  <Select
                    isMulti
                    placeholder={t(
                      'atlas.labels.all_licensors',
                      'All Licensors',
                      'Label for All Licensors',
                    )}
                    options={userLicensors}
                    value={licensorIdValues}
                    onChange={handleLicensorSelectChange}
                    styles={encoreSelectChipStyles}
                    inputId="licensor-multi-select"
                    name="licensor-multi-select"
                    menuIsOpen={licensorMenuIsOpen}
                    onMenuOpen={() => setLicensorMenuOpen(true)}
                    onBlur={() => setLicensorMenuOpen(false)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key.toLowerCase() === 'escape') {
                        setLicensorMenuOpen(false);
                      }
                    }}
                  />
                </StyledFormGroup>
                <StyledFormGroup
                  indicator="optional"
                  label={t('atlas.labels.feeds', 'Feeds', 'Label for Feeds')}
                  labelFor="feed-multi-select"
                >
                  <Select
                    isMulti
                    placeholder={t(
                      'atlas.labels.all_feeds',
                      'All Feeds',
                      'Label for All Feeds',
                    )}
                    options={userFeeds}
                    value={feedIdValues}
                    onChange={handleFeedSelectChange}
                    styles={encoreSelectChipStyles}
                    inputId="feed-multi-select"
                    name="feed-multi-select"
                    menuIsOpen={feedMenuIsOpen}
                    onMenuOpen={() => setFeedMenuOpen(true)}
                    onBlur={() => setFeedMenuOpen(false)}
                    onKeyDown={(event: React.KeyboardEvent) => {
                      if (event.key.toLowerCase() === 'escape') {
                        setFeedMenuOpen(false);
                      }
                    }}
                  />
                </StyledFormGroup>
                <StyledFormGroup
                  indicator="optional"
                  label={t(
                    'atlas.labels.export_name',
                    'Export name',
                    'Input label for Export name (i.e. Weekly album report)',
                  )}
                  labelFor="exportFileName"
                >
                  <FilenameInput
                    value={values.exportFileName}
                    defaultExportFileName={defaultExportFileName}
                    handleChange={handleChange}
                    handleBlur={handleBlur}
                    errors={errors.exportFileName}
                  />
                  {onboardingStep === 3 && (
                    <OnboardingPopoverThree
                      onCompleteOnboarding={onCompleteOnboarding}
                    />
                  )}
                </StyledFormGroup>
              </InputsContainer>
              <HorizontalRule />
              <ButtonContainer>
                {status === FETCH_STATUS.FETCHING ? (
                  <LoadingIndicatorContainer>
                    <LoadingIndicator indicatorSize="md" />
                  </LoadingIndicatorContainer>
                ) : (
                  <ButtonPrimary type="submit">
                    {t(
                      'atlas.actions.create_export',
                      'Create export',
                      'Button text to create catalog export report',
                    )}
                  </ButtonPrimary>
                )}
              </ButtonContainer>
            </Form>
          );
        }}
      </Formik>
    </Content>
  );
};
